import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, track } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("confirming");
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) {
            setState("paid");
            track("booking_complete", { session_id: sessionId });
            const conf = await axios.get(`${API}/bookings/confirmation/${sessionId}`).catch(() => null);
            if (!cancelled && conf) setBooking(conf.data);
          }
          return;
        }
      } catch (e) { /* keep polling */ }
      if (!cancelled && attempts.current < 15) {
        setTimeout(poll, 2000);
      } else if (!cancelled) {
        setState("pending");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-paper" data-testid="payment-success-page">
      <Nav />
      <div className="mx-auto max-w-2xl px-5 md:px-8 pt-36 md:pt-44 pb-28">
        {state === "confirming" && (
          <div data-testid="payment-confirming">
            <p className="font-serif text-3xl md:text-4xl text-ink">Confirming your payment…</p>
            <p className="mt-4 text-ink/60">This usually takes a few seconds.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="booking-confirmation">
            <p className="font-cormorant italic text-lg text-pine">Payment received</p>
            <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink" data-testid="confirmation-heading">
              Your First Walk is booked{booking?.first_name ? `, ${booking.first_name}` : ""}.
            </h1>
            <div className="mt-10 space-y-5 text-base md:text-lg leading-relaxed text-ink/70">
              <p>
                Wear comfortable shoes, bring your headphones and find somewhere
                private enough to talk openly. Walking is encouraged but isn’t
                required.
              </p>
              <p>
                You don’t need to prepare anything complicated. Just come ready
                to talk honestly about what’s happening and what feels most
                important right now.
              </p>
              <p data-testid="confirmation-next-step">
                Your payment is confirmed and your time is booked. Luke will
                call you on WhatsApp at the number you provided when booking.
              </p>
            </div>
            <dl className="mt-12 border-t border-ink/10" data-testid="confirmation-details">
              <div className="flex justify-between gap-6 border-b border-ink/10 py-4">
                <dt className="text-sm text-ink/50">Length</dt>
                <dd className="text-base text-ink">30 minutes</dd>
              </div>
              <div className="flex justify-between gap-6 border-b border-ink/10 py-4">
                <dt className="text-sm text-ink/50">Contact method</dt>
                <dd className="text-base text-ink">WhatsApp call</dd>
              </div>
              <div className="flex justify-between gap-6 border-b border-ink/10 py-4">
                <dt className="text-sm text-ink/50">Date &amp; time</dt>
                <dd className="text-base text-ink text-right">
                  Chosen when you booked
                </dd>
              </div>
            </dl>
            <p className="mt-10 text-sm text-ink/50" data-testid="confirmation-reschedule-note">
              Need to cancel or reschedule? Reply to your confirmation email or
              message Luke directly — see the{" "}
              <Link to="/cancellation" className="underline underline-offset-4 hover:text-ink">
                cancellation policy
              </Link>
              .
            </p>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-pending">
            <h1 className="font-serif text-3xl md:text-4xl text-ink">We’re still confirming your payment.</h1>
            <p className="mt-4 text-ink/60 leading-relaxed">
              Stripe is taking a little longer than usual. If your payment went
              through, your booking is safe — check your email for a receipt and
              come back to this page in a few minutes.
            </p>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-failed">
            <h1 className="font-serif text-3xl md:text-4xl text-ink">We couldn’t confirm this booking.</h1>
            <p className="mt-4 text-ink/60 leading-relaxed">
              The payment link looks incomplete. Please start the booking again.
            </p>
            <Link
              to="/book"
              data-testid="retry-booking-link"
              className="mt-8 inline-flex rounded-full bg-ink text-paper px-8 py-4 text-base font-semibold hover:bg-pine transition-colors duration-300"
            >
              Back to booking
            </Link>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
